<script>
import { sendCompletionCertificateSignNow } from '@/app/api';
import {mapActions} from "vuex";

export default {
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		goToNextStep: {
			type: Function,
			default: () => {}
		}
	},
	data() {
		return {
			app: null,
			isBusy: false,
		};
	},
	computed: {
		/**
		 * Set by the API when the user completes an action below:
		 * - `eSignature` — ROUTE FOR E-SIGNATURE (applicant signs in SignNow; Upload Files does not list CC).
		 * - `print` — GENERATE PDF FOR PRINTING (dealer uploads scanned CC on Upload Files).
		 * Until one of these runs, this stays null and UploadFiles.vue hides the Completion Certificate row.
		 */
		completionMethod() {
			return this.app?.application?.completionCertificate?.method || null;
		},
	},
	async mounted() {
		await this.loadApp();
	},
	methods: {
		...mapActions([
			'saveLoading',
			'showErrorAlert',
			'showSuccessAlert',
			'showWarningAlert'
		]),
		async loadApp() {
			this.saveLoading(true);
			this.app = this.application;
			this.saveLoading(false);
		},
		async routeForEsignature() {
			this.isBusy = true;
			this.saveLoading(true);
			try {
				const data = await sendCompletionCertificateSignNow({ id: this.app?.application?.id, method: 'eSignature' });
				if (data?.errorMessage) {
					this.showErrorAlert(data.errorMessage);
				} else if (data && data.status === 'success') {
					let message = 'Completion certificate e-signature request sent.'
					if (data.url) {
						message = data.message;
					}
					this.showSuccessAlert(message);
					this.app = await this.getApplication();
				} else {
					this.showErrorAlert('Unexpected response from server.');
				}
			} catch (error) {
				const msg = error?.response?.data?.errorMessage || error.message || 'Request failed.';
				this.showErrorAlert(msg);
			}
			this.saveLoading(false);
			this.isBusy = false;
		},
		async generatePdfForPrinting() {
			this.isBusy = true;
			this.saveLoading(true);
			try {
				const data = await sendCompletionCertificateSignNow({ id: this.app?.application?.id, method: 'print' });
				const signatureSlug = data?.url;
				if (data?.errorMessage) {
					this.showErrorAlert(data.errorMessage);
				} else if (data?.signNowUrl || signatureSlug) {
					const win = window.open(data.signNowUrl ?? signatureSlug, '_blank');
					if (!win || win.closed || typeof win.closed === 'undefined') {
						this.showWarningAlert(this.$i18n.t('sign.popupNotice'));
					}
					this.showSuccessAlert('Opening Completion Certificate in a new tab.');
					this.app = await this.getApplication();
				} else {
					let message = "No SignNow link returned.";
					if (this.app?.application?.docuseal_completion_certificate_submission_id) {
						message = "No link returned."
					}
					this.showErrorAlert(message);
				}
			} catch (error) {
				const msg = error?.response?.data?.errorMessage || error.message || 'Request failed.';
				this.showErrorAlert(msg);
			}
			this.saveLoading(false);
			this.isBusy = false;
		},
	},
};
</script>

<template>
	<div>
		<div class="e-signature-template">
			<div class="container-xl">
				<div class="row">
					<div class="col-24">
						<div class="template__page-title">
							<h1>GENERATE COMPLETION CERTIFICATE</h1>
						</div>
						<div class="template__page-description">
							<p>
								Choose a method for executing the Completion Certificate.
							</p>
						</div>
					</div>
				</div>
			</div>
			<div class="mb-5"></div>
			<form @submit.prevent novalidate>
				<div class="container-xl template--vh">
					<div class="row">
						<div class="col-lg-12">
							<div
								:class="'tffi-card tffi-card-main-card e-signature-card ' + ((app?.application && completionMethod === 'eSignature') ? 'active' : '')">
								<div class="row">
									<div class="col-lg-12 d-flex align-items-center">
										<p class="my-0">
											Route the Completion Certificate to applicant 1 for e-signature
										</p>
									</div>
									<div class="col-lg-12 d-flex align-items-center justify-content-center justify-content-xl-end">
										<button type="button" @click="routeForEsignature" :disabled="isBusy"
										        class="tffi-btn tffi-btn--solid theming primary-color mx-auto-lg w-100 fw-bold">
											ROUTE FOR E-SIGNATURE
										</button>
									</div>
								</div>
							</div>
						</div>
						<div class="col-lg-12">
							<div
								:class="'tffi-card tffi-card-main-card e-signature-card ' + ((app?.application && completionMethod === 'print') ? 'active' : '')">
								<div class="row">
									<div class="col-lg-12 d-flex align-items-center">
										<p class="my-0">
											Prepare the Completion Certificate for signing in person
										</p>
									</div>
									<div class="col-lg-12 d-flex align-items-center justify-content-center justify-content-xl-end">
										<button type="button" @click="generatePdfForPrinting" :disabled="isBusy"
										        class="tffi-btn tffi-btn--solid theming primary-color mx-auto-lg w-100 fw-bold">
											GENERATE PDF FOR PRINTING
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.active {
	border: 2px solid var(--tffi-primary-color);
}
</style>
